import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { CountdownBlock } from '#components/document-viewer/countdown/countdown-block';
import {
  normalizeCountdownConfig,
  stringifyCountdownConfig,
  type CountdownEditValues,
} from '#components/document-viewer/countdown/countdown-config';

import {
  NP_COUNTDOWN_TAG,
  parseMarkdownCountdownConfig,
  serializeMarkdownCountdownBlock,
  serializeMarkdownCountdownInnerConfig,
} from './markdown-countdown';

interface MarkdownCountdownNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

function normalizeCountdownAttrs(attrs: unknown) {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};
  return {
    data: normalizeCountdownConfig(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function countdownConfigToEditValues(
  data: ReturnType<typeof normalizeCountdownConfig>,
): CountdownEditValues {
  return {
    type: data.type,
    name: data.name,
    targetAt: data.targetAt,
    cron: data.cron,
    displayUnit: data.displayUnit,
  };
}

function MarkdownCountdownNodeView({
  extension,
  node,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownCountdownNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeCountdownAttrs(node.attrs);

  const handleEdit = () => {
    context.onRequestCountdownEdit?.({
      mode: 'edit',
      initialValues: countdownConfigToEditValues(attrs.data),
      onCommit: async (next) => {
        const normalized = normalizeCountdownConfig(next);
        updateAttributes({
          data: normalized,
          rawConfig: stringifyCountdownConfig(normalized),
          parseError: null,
        });
      },
    });
  };

  return (
    <NodeViewWrapper
      as="span"
      className={
        selected
          ? 'markdown-countdown-node markdown-countdown-node--selected'
          : 'markdown-countdown-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <CountdownBlock
        config={attrs.data}
        parseError={attrs.parseError}
        canEdit={Boolean(context.onRequestCountdownEdit)}
        onEdit={context.onRequestCountdownEdit ? handleEdit : undefined}
      />
    </NodeViewWrapper>
  );
}

export const MarkdownCountdownNode = Node.create<MarkdownCountdownNodeOptions>({
  name: 'npCountdown',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeCountdownConfig({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_COUNTDOWN_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownCountdownConfig(
            element.textContent ?? '',
          );
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeCountdownAttrs(HTMLAttributes);
    return [NP_COUNTDOWN_TAG, serializeMarkdownCountdownInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeCountdownAttrs(node?.attrs);
    return serializeMarkdownCountdownBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownCountdownNodeView);
  },
});
